import {
    CheckCircleOutline,
    Delete,
    Edit,
    ErrorOutline,
} from "@mui/icons-material";
import {
    Box,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxTable, { IAcxTableColumn } from "components/UI/AcxTable/AcxTable";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { observer } from "mobx-react";
import type { EmbeddingDeploymentViewModel } from "models/LLMs/EmbeddingDeploymentViewModel";
import type { EmbeddingModelViewModel } from "models/LLMs/EmbeddingModelViewModel";
import type { CreateEmbeddingModelPayload } from "models/LLMs/LLMManagementModelViewModel";
import type { TestResultViewModel } from "models/LLMs/TestResultViewModel";
import React, { useMemo, useRef, useState } from "react";
import type LLMManagementStore from "stores/LLMManagementStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import AddButton from "../OrganizationDataSources/components/AddButton";

interface DeploymentConfigurationFields {
    deploymentName: string;
    endpointUrl: string;
}

interface DeploymentFormState extends DeploymentConfigurationFields {
    modelId: string;
    hostId: string;
    priority: number;
}

const emptyDeploymentForm: DeploymentFormState = {
    modelId: "",
    hostId: "",
    priority: 0,
    deploymentName: "",
    endpointUrl: "",
};

interface InitialDeploymentFormState extends DeploymentConfigurationFields {
    hostId: string;
    priority: number;
}

const emptyInitialDeploymentForm: InitialDeploymentFormState = {
    hostId: "",
    priority: 0,
    deploymentName: "",
    endpointUrl: "",
};

function isSelfHostedHost(hostType: string | undefined) {
    return hostType === "AcxSelfHosted";
}

function getDeploymentConfigLabel(hostType: string | undefined) {
    return isSelfHostedHost(hostType) ? "Endpoint URL" : "Deployment Name";
}

function getDeploymentConfigHelperText(hostType: string | undefined) {
    return isSelfHostedHost(hostType)
        ? "Self-hosted embedding endpoint"
        : "Azure deployment name";
}

function getDeploymentConfigValue(
    form: DeploymentConfigurationFields,
    hostType: string | undefined,
) {
    return isSelfHostedHost(hostType) ? form.endpointUrl : form.deploymentName;
}

function hasDeploymentConfigValue(
    form: DeploymentConfigurationFields,
    hostType: string | undefined,
) {
    return getDeploymentConfigValue(form, hostType).trim() !== "";
}

function updateDeploymentConfigValue<T extends DeploymentConfigurationFields>(
    form: T,
    value: string,
    hostType: string | undefined,
) {
    return isSelfHostedHost(hostType)
        ? { ...form, endpointUrl: value }
        : { ...form, deploymentName: value };
}

function buildDeploymentPayload(
    form: DeploymentFormState,
    hostType: string | undefined,
) {
    const configuration: Record<string, unknown> = {};
    if (isSelfHostedHost(hostType) && form.endpointUrl) {
        configuration.EndpointUrl = form.endpointUrl;
    } else if (form.deploymentName) {
        configuration.DeploymentName = form.deploymentName;
    }
    return {
        modelId: form.modelId,
        hostId: form.hostId,
        priority: form.priority,
        configuration,
    };
}

function buildInitialDeploymentPayload(
    form: InitialDeploymentFormState,
    hostType: string | undefined,
) {
    const configuration: Record<string, unknown> = {};
    if (isSelfHostedHost(hostType) && form.endpointUrl) {
        configuration.EndpointUrl = form.endpointUrl;
    } else if (form.deploymentName) {
        configuration.DeploymentName = form.deploymentName;
    }
    return {
        hostId: form.hostId,
        priority: form.priority,
        configuration,
    };
}

interface Props {
    store: LLMManagementStore;
}

const EmbeddingModelsTable: React.FC<Props> = observer(({ store }) => {
    const messageStore = useStore(MessageStore);

    // Model state
    const [modelDialogOpen, setModelDialogOpen] = useState(false);
    const [editingModel, setEditingModel] =
        useState<EmbeddingModelViewModel | null>(null);
    const [modelName, setModelName] = useState("");
    const [modelSaving, setModelSaving] = useState(false);
    const [deleteModel, setDeleteModel] =
        useState<EmbeddingModelViewModel | null>(null);

    // Initial deployment state (Create mode only)
    const [initialDeployForm, setInitialDeployForm] =
        useState<InitialDeploymentFormState>(emptyInitialDeploymentForm);
    const [initialDeployTestResult, setInitialDeployTestResult] =
        useState<TestResultViewModel | null>(null);
    const [initialDeployTesting, setInitialDeployTesting] = useState(false);
    const initialDeployTestVersion = useRef(0);

    // Deployment state
    const [deployDialogOpen, setDeployDialogOpen] = useState(false);
    const [editingDeployment, setEditingDeployment] =
        useState<EmbeddingDeploymentViewModel | null>(null);
    const [deployForm, setDeployForm] =
        useState<DeploymentFormState>(emptyDeploymentForm);
    const [deploySaving, setDeploySaving] = useState(false);
    const [deleteDeployment, setDeleteDeployment] =
        useState<EmbeddingDeploymentViewModel | null>(null);
    const [deployTestResult, setDeployTestResult] =
        useState<TestResultViewModel | null>(null);
    const [deployTesting, setDeployTesting] = useState(false);
    // Bumped on every form mutation; in-flight tests check this to ignore
    // stale responses that would otherwise re-enable Save for changed fields.
    const deployTestVersion = useRef(0);

    const getHostType = (hostId: string) =>
        store.hosts.find((h) => h.id === hostId)?.type;

    // Model actions
    const openCreateModel = () => {
        setEditingModel(null);
        setModelName("");
        setInitialDeployForm(emptyInitialDeploymentForm);
        setInitialDeployTestResult(null);
        initialDeployTestVersion.current = 0;
        setModelDialogOpen(true);
    };

    const openEditModel = (model: EmbeddingModelViewModel) => {
        setEditingModel(model);
        setModelName(model.name ?? "");
        setModelDialogOpen(true);
    };

    const closeModelDialog = () => {
        setModelDialogOpen(false);
    };

    const updateInitialDeployForm = (
        updater: (f: InitialDeploymentFormState) => InitialDeploymentFormState,
    ) => {
        setInitialDeployForm((f) => updater(f));
        initialDeployTestVersion.current += 1;
        setInitialDeployTestResult(null);
    };

    const handleTestInitialDeployment = async () => {
        const version = ++initialDeployTestVersion.current;
        setInitialDeployTesting(true);
        try {
            const result = await store.testEmbeddingDeploymentConfig(
                buildInitialDeploymentPayload(
                    initialDeployForm,
                    getHostType(initialDeployForm.hostId),
                ),
            );
            if (version !== initialDeployTestVersion.current) return;
            setInitialDeployTestResult(result);
        } catch {
            if (version !== initialDeployTestVersion.current) return;
            setInitialDeployTestResult({
                success: false,
                message: "Test request failed",
                latencyMs: null,
            });
        } finally {
            if (version === initialDeployTestVersion.current) {
                setInitialDeployTesting(false);
            }
        }
    };

    const handleSaveModel = async () => {
        setModelSaving(true);
        try {
            if (editingModel) {
                const updated = await store.updateEmbeddingModel(
                    editingModel.id,
                    {
                        name: modelName,
                    },
                );
                messageStore.logMessage("Embedding model updated", "success");
                setEditingModel(updated);
            } else {
                const initialDeployment = buildInitialDeploymentPayload(
                    initialDeployForm,
                    getHostType(initialDeployForm.hostId),
                );
                const payload: CreateEmbeddingModelPayload = {
                    name: modelName,
                    initialDeployment,
                };
                const created = await store.createEmbeddingModel(payload);
                messageStore.logMessage("Embedding model created", "success");
                if (created?.id) {
                    setEditingModel(created);
                }
            }
        } catch (error) {
            if (error instanceof Error) {
                messageStore.logError(error.message);
            }
        } finally {
            setModelSaving(false);
        }
    };

    const handleDeleteModel = async (id: string) => {
        try {
            await store.deleteEmbeddingModel(id);
            messageStore.logMessage("Embedding model deleted", "success");
        } catch (error) {
            if (error instanceof Error) {
                messageStore.logError(error.message);
            }
        } finally {
            setDeleteModel(null);
        }
    };

    // Deployment actions
    const resetDeployTest = () => {
        setDeployTestResult(null);
    };

    const openCreateDeployment = () => {
        if (!editingModel) return;
        setEditingDeployment(null);
        setDeployForm({ ...emptyDeploymentForm, modelId: editingModel.id });
        resetDeployTest();
        setDeployDialogOpen(true);
    };

    const openEditDeployment = (deployment: EmbeddingDeploymentViewModel) => {
        setEditingDeployment(deployment);
        setDeployForm({
            modelId: deployment.modelId,
            hostId: deployment.hostId,
            priority: deployment.priority,
            deploymentName: String(
                deployment.configuration?.DeploymentName ??
                    deployment.configuration?.deploymentName ??
                    "",
            ),
            endpointUrl: String(
                deployment.configuration?.EndpointUrl ??
                    deployment.configuration?.endpointUrl ??
                    "",
            ),
        });
        resetDeployTest();
        setDeployDialogOpen(true);
    };

    const updateDeployForm = (
        updater: (f: DeploymentFormState) => DeploymentFormState,
    ) => {
        setDeployForm((f) => updater(f));
        deployTestVersion.current += 1;
        resetDeployTest();
    };

    const handleTestDeploymentForm = async () => {
        const version = ++deployTestVersion.current;
        setDeployTesting(true);
        try {
            const result = await store.testEmbeddingDeploymentConfig(
                buildDeploymentPayload(
                    deployForm,
                    getHostType(deployForm.hostId),
                ),
            );
            if (version !== deployTestVersion.current) return; // stale
            setDeployTestResult(result);
        } catch {
            if (version !== deployTestVersion.current) return; // stale
            setDeployTestResult({
                success: false,
                message: "Test request failed",
                latencyMs: null,
            });
        } finally {
            if (version === deployTestVersion.current) {
                setDeployTesting(false);
            }
        }
    };

    const handleSaveDeployment = async () => {
        setDeploySaving(true);
        try {
            const payload = buildDeploymentPayload(
                deployForm,
                getHostType(deployForm.hostId),
            );
            if (editingDeployment) {
                await store.updateEmbeddingDeployment(
                    editingDeployment.id,
                    payload,
                );
                messageStore.logMessage("Deployment updated", "success");
            } else {
                await store.createEmbeddingDeployment(payload);
                messageStore.logMessage("Deployment created", "success");
            }
            setDeployDialogOpen(false);
        } catch (error) {
            if (error instanceof Error) {
                messageStore.logError(error.message);
            }
        } finally {
            setDeploySaving(false);
        }
    };

    const handleDeleteDeployment = async (id: string) => {
        try {
            await store.deleteEmbeddingDeployment(id);
            messageStore.logMessage("Deployment deleted", "success");
        } catch (error) {
            if (error instanceof Error) {
                messageStore.logError(error.message);
            }
        } finally {
            setDeleteDeployment(null);
        }
    };

    // Model grid columns
    const modelColumns: IAcxTableColumn[] = useMemo(
        () => [
            {
                headerLabel: "Name",
                dataKey: "name",
            },
            {
                headerLabel: "Actions",
                dataKey: "actions",
                formatter: (_value, row: EmbeddingModelViewModel) => (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                            width: "100%",
                        }}
                    >
                        <Tooltip title="Edit">
                            <IconButton
                                size="small"
                                onClick={() => openEditModel(row)}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                onClick={() => setDeleteModel(row)}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
            },
        ],
        [],
    );

    const deploymentColumns: IAcxTableColumn[] = [
        {
            headerLabel: "Host",
            dataKey: "hostName",
        },
        {
            headerLabel: "Deployment / Endpoint",
            dataKey: "configuration.DeploymentName",
            formatter: (_value, row: EmbeddingDeploymentViewModel) =>
                String(
                    row.configuration?.DeploymentName ??
                        row.configuration?.deploymentName ??
                        row.configuration?.EndpointUrl ??
                        row.configuration?.endpointUrl ??
                        "",
                ),
        },
        {
            headerLabel: "Priority",
            dataKey: "priority",
        },
        {
            headerLabel: "Actions",
            dataKey: "actions",
            formatter: (_value, row: EmbeddingDeploymentViewModel) => (
                <Box
                    sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end",
                        width: "100%",
                    }}
                >
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={() => openEditDeployment(row)}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={() => setDeleteDeployment(row)}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    const modelNameValid = (modelName ?? "").trim() !== "";
    const initialDeployHostType = getHostType(initialDeployForm.hostId);
    const deployHostType = getHostType(deployForm.hostId);

    const initialDeployFieldsValid =
        initialDeployForm.hostId !== "" &&
        hasDeploymentConfigValue(initialDeployForm, initialDeployHostType);

    const canTestInitialDeployment =
        initialDeployFieldsValid && !initialDeployTesting;

    const canSaveModel = editingModel
        ? modelNameValid
        : modelNameValid &&
          initialDeployFieldsValid &&
          initialDeployTestResult?.success === true;

    const canTestDeployment =
        deployForm.modelId !== "" &&
        deployForm.hostId !== "" &&
        hasDeploymentConfigValue(deployForm, deployHostType) &&
        !deployTesting;

    const canSaveDeployment =
        deployForm.modelId !== "" &&
        deployForm.hostId !== "" &&
        deployTestResult?.success === true;

    const hostOptions = useMemo(
        () => store.hosts.map((h) => ({ value: h.id, label: h.name })),
        [store.hosts],
    );

    const deploymentsForEditingModel = editingModel
        ? store.embeddingDeployments.filter(
              (d) => d.modelId === editingModel.id,
          )
        : [];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Models Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        width: "100%",
                    }}
                >
                    <AddButton onClick={openCreateModel}>
                        Embedding Model
                    </AddButton>
                </Box>
                <AcxTable
                    columns={modelColumns}
                    rows={store.embeddingModels}
                    enableHover
                    cellDense
                />
            </Box>

            {/* Model Create/Edit Dialog */}
            <AcxDialog
                title={
                    editingModel
                        ? "Edit Embedding Model"
                        : "Add Embedding Model"
                }
                isOpen={modelDialogOpen}
                onClose={closeModelDialog}
                maxWidth={false}
                contentWidth="min(1200px, calc(100vw - 96px))"
                dialogContentChildren={
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            pt: 1,
                        }}
                    >
                        <AcxMainTextField
                            id="embedding-model-name"
                            labelText="Name"
                            required
                            fullWidth
                            value={modelName}
                            onChange={(e) => setModelName(e.target.value)}
                        />

                        {!editingModel && (
                            <Box
                                sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${theme.palette.gray[200]}`,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: 600,
                                        color: theme.palette.gray[700],
                                    }}
                                >
                                    First Deployment
                                </Typography>
                                <Box>
                                    <AcxSelect
                                        id="initial-emb-deployment-host"
                                        inputLabel="Host"
                                        fullWidth
                                        required
                                        options={hostOptions}
                                        defaultValue={hostOptions.find(
                                            (o) =>
                                                o.value ===
                                                initialDeployForm.hostId,
                                        )}
                                        onChange={(opt) =>
                                            updateInitialDeployForm((f) => ({
                                                ...f,
                                                hostId: opt?.value ?? "",
                                            }))
                                        }
                                    />
                                </Box>
                                <AcxMainTextField
                                    id="initial-emb-deployment-name"
                                    labelText={getDeploymentConfigLabel(
                                        initialDeployHostType,
                                    )}
                                    required
                                    fullWidth
                                    value={getDeploymentConfigValue(
                                        initialDeployForm,
                                        initialDeployHostType,
                                    )}
                                    onChange={(e) =>
                                        updateInitialDeployForm((f) =>
                                            updateDeploymentConfigValue(
                                                f,
                                                e.target.value,
                                                initialDeployHostType,
                                            ),
                                        )
                                    }
                                    helperText={getDeploymentConfigHelperText(
                                        initialDeployHostType,
                                    )}
                                />
                                <AcxMainTextField
                                    id="initial-emb-deployment-priority"
                                    labelText="Priority"
                                    type="number"
                                    fullWidth
                                    value={initialDeployForm.priority}
                                    onChange={(e) =>
                                        updateInitialDeployForm((f) => ({
                                            ...f,
                                            priority:
                                                parseInt(e.target.value, 10) ||
                                                0,
                                        }))
                                    }
                                />
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <AcxButton
                                        color="white"
                                        onClick={handleTestInitialDeployment}
                                        loading={initialDeployTesting}
                                        disabled={!canTestInitialDeployment}
                                    >
                                        Test
                                    </AcxButton>
                                    {!initialDeployTestResult && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Run Test before creating. Create is
                                            disabled until a successful test.
                                        </Typography>
                                    )}
                                </Box>
                                {initialDeployTestResult && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            p: 1,
                                            borderRadius: 1,
                                            backgroundColor:
                                                initialDeployTestResult.success
                                                    ? theme.palette.success
                                                          .light
                                                    : theme.palette.error.light,
                                            color: initialDeployTestResult.success
                                                ? theme.palette.success
                                                      .contrastText
                                                : theme.palette.error
                                                      .contrastText,
                                        }}
                                    >
                                        {initialDeployTestResult.success ? (
                                            <CheckCircleOutline fontSize="small" />
                                        ) : (
                                            <ErrorOutline fontSize="small" />
                                        )}
                                        <Typography variant="body2">
                                            {initialDeployTestResult.message}
                                            {initialDeployTestResult.latencyMs !=
                                                null &&
                                                ` (${initialDeployTestResult.latencyMs}ms)`}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {editingModel && (
                            <Box
                                sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${theme.palette.gray[200]}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            color: theme.palette.gray[700],
                                            marginRight: "auto",
                                        }}
                                    >
                                        Deployments
                                    </Typography>
                                    <AddButton onClick={openCreateDeployment}>
                                        Deployment
                                    </AddButton>
                                </Box>
                                {deploymentsForEditingModel.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ p: 1 }}
                                    >
                                        No deployments yet. Add one to make this
                                        model usable.
                                    </Typography>
                                ) : (
                                    <AcxTable
                                        columns={deploymentColumns}
                                        rows={deploymentsForEditingModel}
                                        enableHover
                                        cellDense
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                }
            >
                <AcxButton
                    color="primary"
                    onClick={handleSaveModel}
                    loading={modelSaving}
                    disabled={!canSaveModel}
                >
                    {editingModel ? "Update" : "Create"}
                </AcxButton>
            </AcxDialog>

            {/* Deployment Create/Edit Dialog */}
            <AcxDialog
                title={
                    editingDeployment ? "Edit Deployment" : "Add Deployment"
                }
                isOpen={deployDialogOpen}
                onClose={() => setDeployDialogOpen(false)}
                maxWidth={false}
                contentWidth="min(800px, calc(100vw - 96px))"
                dialogContentChildren={
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            pt: 1,
                        }}
                    >
                        <Box>
                            <AcxSelect
                                id="emb-deployment-host"
                                inputLabel="Host"
                                fullWidth
                                required
                                options={hostOptions}
                                defaultValue={hostOptions.find(
                                    (o) => o.value === deployForm.hostId,
                                )}
                                onChange={(opt) =>
                                    updateDeployForm((f) => ({
                                        ...f,
                                        hostId: opt?.value ?? "",
                                    }))
                                }
                            />
                        </Box>
                        <AcxMainTextField
                            id="emb-deployment-name"
                            labelText={getDeploymentConfigLabel(
                                deployHostType,
                            )}
                            fullWidth
                            value={getDeploymentConfigValue(
                                deployForm,
                                deployHostType,
                            )}
                            onChange={(e) =>
                                updateDeployForm((f) =>
                                    updateDeploymentConfigValue(
                                        f,
                                        e.target.value,
                                        deployHostType,
                                    ),
                                )
                            }
                            helperText={getDeploymentConfigHelperText(
                                deployHostType,
                            )}
                        />
                        <AcxMainTextField
                            id="emb-deployment-priority"
                            labelText="Priority"
                            type="number"
                            fullWidth
                            value={deployForm.priority}
                            onChange={(e) =>
                                updateDeployForm((f) => ({
                                    ...f,
                                    priority:
                                        parseInt(e.target.value, 10) || 0,
                                }))
                            }
                        />

                        {deployTestResult && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: 1,
                                    borderRadius: 1,
                                    backgroundColor: deployTestResult.success
                                        ? theme.palette.success.light
                                        : theme.palette.error.light,
                                    color: deployTestResult.success
                                        ? theme.palette.success.contrastText
                                        : theme.palette.error.contrastText,
                                }}
                            >
                                {deployTestResult.success ? (
                                    <CheckCircleOutline fontSize="small" />
                                ) : (
                                    <ErrorOutline fontSize="small" />
                                )}
                                <Typography variant="body2">
                                    {deployTestResult.message}
                                    {deployTestResult.latencyMs != null &&
                                        ` (${deployTestResult.latencyMs}ms)`}
                                </Typography>
                            </Box>
                        )}
                        {!deployTestResult && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Run Test to validate the configuration. Save is
                                disabled until a successful test against the
                                current values.
                            </Typography>
                        )}
                    </Box>
                }
            >
                <AcxButton
                    color="white"
                    onClick={handleTestDeploymentForm}
                    loading={deployTesting}
                    disabled={!canTestDeployment}
                >
                    Test
                </AcxButton>
                <AcxButton
                    color="primary"
                    onClick={handleSaveDeployment}
                    loading={deploySaving}
                    disabled={!canSaveDeployment}
                >
                    {editingDeployment ? "Update" : "Create"}
                </AcxButton>
            </AcxDialog>

            {/* Delete Model Confirmation */}
            <AcxDialog
                title="Delete Embedding Model"
                text="Are you sure? Models with active deployments cannot be deleted."
                isOpen={deleteModel !== null}
                onClose={() => setDeleteModel(null)}
                contentWidth="400px"
                dialogContentChildren={
                    deleteModel && (
                        <Typography
                            sx={{
                                color: theme.palette.gray[500],
                                fontWeight: 600,
                            }}
                        >
                            - {deleteModel.name}
                        </Typography>
                    )
                }
            >
                <AcxButton
                    color="primary"
                    onClick={() =>
                        deleteModel && handleDeleteModel(deleteModel.id)
                    }
                >
                    Delete
                </AcxButton>
            </AcxDialog>

            {/* Delete Deployment Confirmation */}
            <AcxDialog
                title="Delete Deployment"
                text="Are you sure you want to delete this deployment?"
                isOpen={deleteDeployment !== null}
                onClose={() => setDeleteDeployment(null)}
                contentWidth="400px"
                dialogContentChildren={
                    deleteDeployment && (
                        <Typography
                            sx={{
                                color: theme.palette.gray[500],
                                fontWeight: 600,
                            }}
                        >
                            - {deleteDeployment.hostName} (
                            {String(
                                deleteDeployment.configuration
                                    ?.DeploymentName ??
                                    deleteDeployment.id.substring(0, 8),
                            )}
                            )
                        </Typography>
                    )
                }
            >
                <AcxButton
                    color="primary"
                    onClick={() =>
                        deleteDeployment &&
                        handleDeleteDeployment(deleteDeployment.id)
                    }
                >
                    Delete
                </AcxButton>
            </AcxDialog>
        </Box>
    );
});

export default EmbeddingModelsTable;
